/**
 * Payments Wave 0 (FIX-PLAN 2026-10-18) — pure decisions for the live pricing page, kept out of the component so
 * every SCENARIOS row is a unit test:
 *   - W0-12 (2): what to offer given `GET /payments/status` (the server's word; the UI never offers a purchase the
 *     server would refuse) — `decideGate`.
 *   - W0-1 step 7 / W0-10: how to read a failed `POST /payments/pay` — `classifyPayError`.
 */
import type {CheckoutStatus} from '../../../services/payments.service';
import {planLabel, providerLabel} from '../../portal/payments/checkout-format';
import {
    changeTo,
    CHECKOUT_IN_PROGRESS,
    crossGateway,
    PAYPAL_FINISHING_PREVIOUS,
    paymentUpdateRequired,
    PLAN_CHANGE_PAYPAL_UNAVAILABLE,
    UPDATE_YOUR_CARD,
} from '../../portal/payments/checkout-messages';

export type GateDecision =
    /** Today's payment tabs (PayPal buttons / card form). */
    | {kind: 'buttons'}
    /** OD-14: the selected plan is the one the user already pays for → one disabled "Current plan" button. */
    | {kind: 'current-plan'}
    /** Stripe subscriber on another plan → "Change to <Plan>" (W0-4 `POST /payments/change-plan`). */
    | {kind: 'change-plan'; label: string}
    /** PayPal subscriber on another plan → support message (W2-4 implements it). */
    | {kind: 'paypal-support'; message: string}
    /** Live subscription at the other gateway → block before any button. */
    | {kind: 'cross-gateway'; message: string}
    /** past_due / SUSPENDED → "Update your card" link; no new subscription. */
    | {kind: 'payment-update'; message: string; linkLabel: string; href: string}
    /** Another submit holds the checkout lease. */
    | {kind: 'in-progress'; message: string}
    /** PA-4: a PayPal sub of ours APPROVED/ACTIVE at PayPal, not yet activated locally. */
    | {kind: 'finishing-paypal'; message: string};

const SUBSCRIBER_STATES = new Set(['ACTIVE', 'PAST_DUE', 'PAUSED', 'CANCELED_ACCESS_UNTIL_PERIOD_END']);

/**
 * True when the server's `(planName, interval)` names the page's selected plan. The server may say `patron_monthly`
 * (gateway-agnostic plan name) or the tier `PATRON` + `interval` (`MONTHLY` | `ANNUAL` | `YEARLY`).
 */
export function isSamePlan(planName: string | null | undefined, interval: string | null | undefined, selectedPlanId: string): boolean {
    if (!planName) return false;
    const p = planName.toLowerCase();
    const sel = selectedPlanId.toLowerCase();
    if (p === sel) return true;
    const tier = p === 'patron' || p === 'pro' ? 'patron' : p === 'backer' || p === 'basic' ? 'backer' : null;
    if (!tier || !interval) return false;
    const i = interval.toUpperCase();
    const cycle = i === 'ANNUAL' || i === 'YEARLY' || i === 'YEAR' ? 'yearly' : i === 'MONTHLY' || i === 'MONTH' ? 'monthly' : null;
    return cycle !== null && `${tier}_${cycle}` === sel;
}

export function decideGate(status: CheckoutStatus | null | undefined, selectedPlanId: string): GateDecision {
    if (!status) return {kind: 'buttons'};
    const blocked = (status.blockedReason || '').toUpperCase();
    if (blocked === 'CROSS_GATEWAY') {
        return {kind: 'cross-gateway', message: crossGateway(providerLabel(status.provider) || 'different')};
    }
    if (blocked === 'PAYMENT_UPDATE_REQUIRED') {
        return {
            kind: 'payment-update',
            message: paymentUpdateRequired(planLabel(status.planName) || 'subscription'),
            linkLabel: UPDATE_YOUR_CARD,
            href: status.portalUrl || '/portal/plan',
        };
    }
    if (blocked === 'IN_PROGRESS') return {kind: 'in-progress', message: CHECKOUT_IN_PROGRESS};
    // PA-4 / PA-22: only APPROVED/ACTIVE-not-local carries `pendingSubscriptionId`; a plain APPROVAL_PENDING row
    // (popup closed) is null here and the buttons show normally.
    if (status.pendingSubscriptionId) return {kind: 'finishing-paypal', message: PAYPAL_FINISHING_PREVIOUS};
    const state = (status.planState || '').toUpperCase();
    if (SUBSCRIBER_STATES.has(state) && status.planName) {
        if (isSamePlan(status.planName, status.interval, selectedPlanId)) return {kind: 'current-plan'};
        const provider = (status.provider || '').toUpperCase();
        if (provider === 'STRIPE' && status.canChangePlan) {
            return {kind: 'change-plan', label: changeTo(planLabel(selectedPlanId))};
        }
        if (provider === 'PAYPAL') return {kind: 'paypal-support', message: PLAN_CHANGE_PAYPAL_UNAVAILABLE};
    }
    return {kind: 'buttons'};
}

export type PayErrorClass =
    /** W0-1 step 3: 402 — the card was declined before anything was persisted; nothing charged. */
    | {kind: 'declined'; message: string}
    /** W0-10: today's B1 409 ("duplicate cancelled") — the card WAS charged; say so, with a support reference. */
    | {kind: 'duplicate-charged'}
    | {kind: 'other'};

const LEGACY_DUPLICATE_RE = /already have a subscription; duplicate cancelled/i;

/**
 * CR-F1-1 (QA-4): the server's refusal `code` (= `errorCode`, both keys) — on the parsed body (`ClientError.response`)
 * or, for callers that already unwrapped it, on the error itself. Undefined when the build did not send one.
 */
export function errorCodeOf(err: unknown): string | undefined {
    const e = err as {code?: unknown; errorCode?: unknown; response?: {code?: unknown; errorCode?: unknown} | null} | null;
    if (!e || typeof e !== 'object') return undefined;
    const c = e.response?.code ?? e.response?.errorCode ?? e.code ?? e.errorCode;
    return typeof c === 'string' && c ? c : undefined;
}

const GENERIC_DECLINED = 'Your card was declined. No charge was made — try another card.';

export function classifyPayError(err: unknown): PayErrorClass {
    const e = err as {status?: number; message?: string} | null;
    if (!e || typeof e !== 'object') return {kind: 'other'};
    // CR-F1-1: `code: CARD_DECLINED` is the same refusal whatever the status line says (W0-1 step 3 sends 402 + code).
    if (e.status === 402 || errorCodeOf(e) === 'CARD_DECLINED') {
        return {kind: 'declined', message: stripElapsed(e.message) || GENERIC_DECLINED};
    }
    if (e.status === 409 && LEGACY_DUPLICATE_RE.test(e.message || '')) return {kind: 'duplicate-charged'};
    return {kind: 'other'};
}

/** Belt and braces for a message produced by a build that still appends " (NNNms)". */
function stripElapsed(message: string | undefined): string {
    return (message || '').replace(/\s\(\d+ms\)\s*$/, '');
}

export type PlanChangeErrorClass =
    /** `ALREADY_ON_PLAN` (409): the server already has this user on the selected plan — re-GET `/payments/status`, the gate shows "Current plan". */
    | {kind: 'already-on-plan'; message: string}
    /** `PAYMENT_UPDATE_REQUIRED` (409): past_due / SUSPENDED — re-GET `/payments/status`, the gate shows the "Update your card" link. */
    | {kind: 'payment-update-required'; message: string}
    /** `CARD_DECLINED` (402) on the proration invoice: the checkout's declined dialog (same `code` as `/payments/pay`). */
    | {kind: 'declined'; message: string}
    /** Everything else (`PLAN_CHANGE_UNAVAILABLE`, `UNKNOWN_PLAN`, flag off, network): the server message via handleError, as today. */
    | {kind: 'other'};

/** CR-F1-1 (QA-4): how to read a refused `POST /payments/change-plan` (preview or apply) — keyed on `code`, never on text. */
export function classifyPlanChangeError(err: unknown): PlanChangeErrorClass {
    const e = err as {status?: number; message?: string} | null;
    if (!e || typeof e !== 'object') return {kind: 'other'};
    const message = stripElapsed(e.message);
    switch (errorCodeOf(e)) {
        case 'ALREADY_ON_PLAN':
            return {kind: 'already-on-plan', message: message || 'You are already on this plan. Nothing was charged.'};
        case 'PAYMENT_UPDATE_REQUIRED':
            return {kind: 'payment-update-required', message: message || 'Update your payment method first. Nothing was charged.'};
        case 'CARD_DECLINED':
            return {kind: 'declined', message: message || GENERIC_DECLINED};
        default:
            return {kind: 'other'};
    }
}
